import React, { useState, useEffect, useMemo } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import HeaderSection from "../components/header-section"
import Sidebar from "../features/sidebar/components/sidebar"
import OnboardingSteps from "../components/OnboardingSteps"
import { useTypedSelector } from "../app/store"
import {
  useGetMeasuresQuery,
  useGetOrganizationMeasuresQuery,
  useCreateOrganizationMeasureMutation,
  useGetOnboardingCompletionQuery,
  useUpdateSelectedScenarioMutation,
} from "../app/services/meseeknaApiV2"
import { trackOnboardingStep } from "../lib/analytics"

// Type for measure display (transformed from API response)
type MeasureDisplay = {
  id: number
  name: string
  color: string
  definition: string
}

// Default fallback color for measures without a color in DB
const DEFAULT_MEASURE_COLOR = "#6699FF"

// Sort order map for measure wheel display (replicates original hardcoded order)
// This creates the color gradient: yellow → green → blue → purple → pink → orange
const MEASURE_SORT_ORDER: Record<string, number> = {
  "developmental orientation": 1,
  "development orientation": 1, // alias
  "dependability": 2,
  "innovation": 3,
  "creative decisiveness": 4,
  "creative flexibility": 5,
  "workplace engagement": 6,
  "people centrism": 7,
  "communication": 8,
  "empathetic communication": 9,
  "collaboration": 10,
  "team orientation": 11,
  "conflict approach": 12,
  "conflict response": 13,
  "conflict management": 14,
  "conflict resolution": 14, // alias
  "emotional resilience": 15,
  "breadth of approach": 16,
  "resource management": 17,
  "crisis preparedness": 18,
  "crisis response": 19,
  "crisis recovery": 20,
  "task management": 21,
  "information management": 22,
  "strategic quantitative reasoning": 23,
  "strategic quantitative thinking": 23, // alias
  "strategic thinking": 24,
  "advanced strategy": 25,
  "productivity": 26,
  "proactivity": 27,
  "initiative": 28,
  "goal orientation": 29,
  "goal management": 30,
}

// Exact VALUE_KEYWORDS mapping from RTF file (exact structure)
const VALUE_KEYWORDS = {
  1: [
    "excellence",
    "passion",
    "champion's heart",
    "learn",
    "develop",
    "continuous learning",
    "adapt",
    "growth mindset",
    "self-improvement",
    "professional growth",
    "never stop learning",
    "personal development",
    "continuous improvement",
    "strive for excellence",
    "pursuit of excellence",
    "raise the bar",
    "best in class",
    "learn through discovery",
    "excellence to change the status quo",
    "lifelong learning",
    "growth hacker",
    "T shaped skills",
    "upskilling",
    "reskilling",
    "learning organization",
    "skill agility",
    "learning",
    "growth",
    "development",
    "education",
    "coaching",
    "mentoring",
    "skill development",
    "talent development",
    "progress",
    "advancement",
    "training",
    "capability building",
    "progression",
    "evolution",
    "knowledge acquisition",
    "improvement",
    "expertise",
    "mastery",
    "achievement",
    "competence",
    "knowledge sharing",
    "professional development",
    "career development",
    "agile learning",
    "development focus",
    "human capital development",
    "raising standards",
    "competency",
    "self-direction",
    "self-reliance",
  ],
  2: [
    "integrity",
    "trust",
    "credibility",
    "honesty",
    "reliable",
    "accountable",
    "responsible",
    "consistent",
    "act with integrity",
    "ferocious integrity",
    "integrity and respect",
    "professionalism",
    "serve with integrity",
    "trust and personal responsibility",
    "radical transparency",
    "authentically ethical",
    "business ethics",
    "ethical AI",
    "responsible innovation",
    "sustainable practice",
    "ethical leadership",
    "trust by design",
    "zero trust",
    "reliability champion",
    "trust builder",
    "integrity focused",
    "ethical excellence",
    "accountability driven",
    "responsible business",
    "trustworthy practice",
    "ethical conduct",
    "integrity first",
    "responsible action",
    "accountability focused",
    "trust driven",
    "reliability focused",
    "ethical mindset",
    "integrity oriented",
    "trust centered",
    "accountability champion",
    "responsible practice",
    "trust excellence",
    "trustworthiness",
    "ethics",
    "reliability",
    "commitment",
    "dedication",
    "follow through",
    "conscientiousness",
    "duty",
    "fidelity",
    "honor",
    "moral compass",
    "transparency",
    "compliance",
    "fairness",
    "justice",
    "principled",
    "virtue",
    "ethical",
    "authenticity",
    "candor",
    "sincerity",
    "truthfulness",
  ],
  3: [
    "challenge assumptions",
    "challenge ideas",
    "challenge status quo",
    "curious",
    "disruptive",
    "innovation",
    "reimagine",
    "creative solutions",
    "think differently",
    "breakthrough",
    "pioneering",
    "collective ingenuity",
    "curious builders",
    "discovery and choice",
    "disruptive mentorship",
    "find a way",
    "freedom",
    "ideas",
    "let the best idea win",
    "relentless curiosity",
    "seek, challenge, and explore",
    "strive for breakthroughs",
    "unique individuals",
    "cultivate diversity of thought",
    "define new possibilities",
    "10x thinking",
    "moonshot thinking",
    "fail fast",
    "move fast and learn",
    "zero to one",
    "first principles",
    "digital first",
    "tech forward",
    "future ready",
    "innovation mindset",
    "embrace experimentation",
    "breakthrough innovation",
    "disrupt the status quo",
    "pioneer the future",
    "radical innovation",
    "thoughtful disruption",
    "innovate at scale",
    "transform the industry",
    "fail fast learn faster",
    "move fast break things",
    "zero to one thinking",
    "first principles thinking",
    "digital transformation",
    "tech forward thinking",
    "future ready mindset",
    "innovation at scale",
    "embrace the unknown",
    "breakthrough thinking",
    "disruptive excellence",
    "pioneer mindset",
    "innovative mindset",
    "spark creativity",
    "catalyze change",
    "revolutionary thinking",
    "outside the box solutions",
    "paradigm shifting",
    "blue sky thinking",
    "push boundaries",
    "challenge convention",
    "creativity",
    "inventiveness",
    "originality",
    "experimentation",
    "exploration",
    "disruption",
    "transformation",
    "design thinking",
    "ingenuity",
    "imagination",
    "invention",
    "novelty",
    "creative thinking",
    "discovery",
    "out of box thinking",
    "divergent thinking",
    "idea generation",
    "inquisitiveness",
    "inquiry",
    "creative process",
    "innovate",
    "unconventional",
    "groundbreaking",
    "visionary",
    "ideation",
  ],
  4: [
    "act fast",
    "preempt",
    "experiment",
    "be ambitious",
    "be direct",
    "deliver results",
    "decision-making",
    "initiative",
    "action",
    "determination",
    "courage",
    "boldness",
    "assertiveness",
    "decisiveness",
    "fast decision-making",
    "judgment",
    "critical thinking",
    "deliberation",
    "acting with conviction",
    "risk-taking",
    "calculated risk",
    "logical thinking",
    "analytical thinking",
    "quick response",
    "promptness",
    "responsive action",
    "out of box thinking",
    "solution focus",
    "independent decisions",
    "careful analysis",
    "formative defiance",
  ],
  5: [
    "challenge assumptions",
    "challenge ideas",
    "cultivate diversity of thought",
    "adaptability",
    "flexibility",
    "agility",
    "versatility",
    "open-mindedness",
    "receptiveness",
    "acceptance",
    "change",
    "openness to change",
    "nimbleness",
    "responsiveness",
    "adjustability",
    "pivoting",
    "resilience",
    "adaptiveness",
    "learning agility",
    "mental agility",
    "fluid thinking",
    "shift thinking patterns",
    "shift styles of functioning",
    "environmental changes",
    "shift gears",
    "current needs",
    "agile mindset",
    "sprint mentality",
    "iterate rapidly",
    "quick pivots",
    "adapt or die",
    "change ready",
    "pivot ready",
    "growth ready",
  ],
  6: [
    "hands-on delivery",
    "hustle responsibly",
    "energy and enthusiasm",
    "remote first",
    "hybrid ready",
    "work life harmony",
    "flexible workplace",
    "distributed teams",
    "async first",
    "globally connected",
    "human centered workplace",
    "work from anywhere",
    "engagement",
    "commitment",
    "passion",
    "drive",
    "dedication",
    "motivation",
    "energy",
    "enthusiasm",
    "involvement",
    "participation",
    "active contribution",
    "devotion",
    "zeal",
    "vigor",
    "work life balance",
    "employee wellbeing",
    "employee satisfaction",
    "intensity",
    "dynamism",
    "hands on",
    "focus",
    "active participation",
    "full involvement",
    "immersion",
    "ownership",
    "team player",
    "all in attitude",
  ],
  7: [
    "respect for individuals",
    "lead with heart",
    "build relationships",
    "people",
    "human-centric",
    "inclusive",
    "employee-first",
    "foster inclusion",
    "generosity",
    "modesty",
    "passionate service",
    "value and respect all perspectives",
    "bring your whole self",
    "psychological safety",
    "belong anywhere",
    "radical inclusivity",
    "inclusive by design",
    "equity focused",
    "diversity wins",
    "intersectional inclusion",
    "cultural intelligence",
    "celebrate differences",
    "allyship",
    "cultural competence",
    "representation matters",
    "people first mindset",
    "human centered approach",
    "inclusive excellence",
    "diversity champion",
    "cultural awareness",
    "belonging champion",
    "equity advocate",
    "inclusion catalyst",
    "psychological safety champion",
    "empathy advocate",
    "people development",
    "talent focused",
    "human capital excellence",
    "relationship builder",
    "community minded",
    "human experience",
    "people powered",
    "talent centric",
    "human focused design",
    "customer obsessed",
    "user first",
    "customer centric",
    "user centric",
    "customer empathy",
    "user empathy",
    "customer advocacy",
    "empathy",
    "compassion",
    "caring",
    "kindness",
    "understanding",
    "consideration",
    "dignity",
    "diversity",
    "equality",
    "belonging",
    "humanity",
    "sensitivity",
    "support",
    "helpfulness",
    "listening",
    "inclusivity",
    "service",
    "care",
    "thoughtfulness",
    "acceptance",
    "accessibility",
    "altruism",
    "benevolence",
  ],
  8: [
    "authentic relationships",
    "communication",
    "express ideas",
    "dialogue",
    "expression",
    "clarity",
    "information sharing",
    "openness",
    "feedback",
    "discourse",
    "eloquence",
    "conciseness",
    "precision",
    "effective communication",
    "clear messaging",
    "radical candor",
    "transparent communication",
    "open communication",
    "feedback rich",
    "feedback culture",
    "two way dialogue",
    "active listening",
    "straight talk",
    "no BS",
    "transparent dialogue",
    "open conversation",
    "feedback driven",
    "communication excellence",
    "active dialogue",
    "straight talk culture",
    "direct communication",
    "two way engagement",
    "open door policy",
    "collaborative dialogue",
    "honest conversation",
    "authentic communication",
    "meaningful dialogue",
    "impactful messaging",
    "clear articulation",
    "transparent sharing",
    "open feedback",
    "constructive dialogue",
  ],
  9: [
    "authentic relationships",
    "build relationships",
    "empathy",
    "empathetic listening",
    "emotional intelligence",
    "supportive communication",
    "compassionate dialogue",
    "mindful interaction",
    "active listening",
    "deep listening",
    "heart-centered communication",
    "compassionate feedback",
    "emotional awareness",
    "supportive language",
    "inclusive dialogue",
    "considerate discussion",
    "understanding communication",
    "interpersonal sensitivity",
    "connected conversations",
    "relationship-focused communication",
    "supportive feedback",
    "mindful messaging",
    "compassionate clarity",
    "attentive dialogue",
  ],
  10: [
    "actively collaborate",
    "collaborate",
    "collaborative",
    "cross functional",
    "borderless collaboration",
    "networked teams",
    "squad based",
    "agile teams",
    "flat hierarchy",
    "collaborative networks",
    "ecosystem thinking",
    "seamless collaboration",
    "cross functional excellence",
    "team synergy",
    "collaborative innovation",
    "partnership mindset",
    "alliance building",
    "collaborative spirit",
    "team oriented",
    "partnership focused",
    "collaborative excellence",
    "team synergy driven",
    "alliance focused",
    "partnership driven",
    "team collaboration",
    "collaborative mindset",
    "synergy focused",
    "team excellence",
    "collaborative mastery",
    "partnership excellence",
    "team oriented approach",
    "cooperation",
    "partnership",
    "alliance",
    "synergy",
    "joint effort",
    "togetherness",
    "coordination",
    "collaborative work",
    "collaborative spirit",
  ],
  11: [
    "solidarity",
    "take care of each other",
    "teaming",
    "teamwork",
    "collective",
    "one team",
    "work together",
    "team spirit",
    "stronger together",
    "collective success",
    "work as one",
    "every voice is heard",
    "everyone is expressing their opinions",
    "unity",
    "mutual support",
    "shared vision",
    "camaraderie",
    "group cohesion",
    "collective identity",
    "mutual support",
    "team harmony",
    "unified effort",
    "collective achievement",
    "group success",
    "team dynamics",
    "group synergy",
    "team cohesion",
    "team support",
    "group loyalty",
    "collective ownership",
    "team dedication",
    "shared success",
    "group commitment",
    "united efforts",
    "team bonds",
    "group pride",
    "team identity",
    "collective focus",
  ],
  12: [
    "challenge and understand",
    "challenge ideas",
    "conflict awareness",
    "preventive action",
    "issue identification",
    "early intervention",
    "constructive confrontation",
    "problem anticipation",
    "conflict readiness",
    "tension recognition",
    "conflict navigation",
    "proactive problem-solving",
    "preventive dialogue",
    "potential issue awareness",
    "constructive tension",
    "healthy confrontation",
    "disagreement recognition",
    "conflict preparation",
    "issue sensitivity",
    "situation awareness",
    "timing sensitivity",
    "strategic confrontation",
    "appropriate intervention",
    "conflict readiness",
    "problem sensitivity",
    "timely intervention",
    "issue spotting",
    "problem awareness",
  ],
  13: [
    "build relationships",
    "care",
    "empathy",
    "conflict handling",
    "resolution skills",
    "mediation abilities",
    "de-escalation",
    "tension reduction",
    "problem-solving",
    "reconciliation",
    "compromise",
    "negotiation",
    "peace-making",
    "careful communication",
    "transparency in conflict",
    "stakeholder awareness",
    "strategic navigation",
    "compassionate response",
    "mediative approach",
    "balanced reaction",
    "conflict confidence",
    "resolution focus",
    "diplomatic handling",
    "thoughtful engagement",
    "problem-solving mindset",
    "solution-focused response",
    "inclusive resolution",
    "needs-based approach",
    "win-win orientation",
    "constructive engagement",
  ],
  14: [
    "challenge and understand",
    "build relationships",
    "conflict resolution",
    "dispute settlement",
    "problem resolution",
    "tension management",
    "disagreement handling",
    "reconciliation process",
    "mediation skills",
    "negotiation expertise",
    "facilitation abilities",
    "resolution expertise",
    "multi-dimensional solutions",
    "thoughtful negotiations",
    "future planning",
    "implementation skills",
    "resolution agendas",
    "sustainable solutions",
    "long-term resolution",
    "comprehensive settlement",
    "stakeholder satisfaction",
    "relationship preservation",
    "constructive outcomes",
    "value creation through conflict",
    "growth through disagreement",
    "learning from conflict",
    "collaborative resolution",
    "creative problem-solving",
    "solution engineering",
    "resolution architecture",
  ],
  15: [
    "grit and curiosity",
    "hustle responsibly",
    "resilience",
    "grit",
    "persistence",
    "endurance",
    "fortitude",
    "tenacity",
    "hardiness",
    "mental toughness",
    "emotional strength",
    "adaptability",
    "recovery ability",
    "bouncing back",
    "stress management",
    "coping skills",
    "perseverance",
    "determination",
    "steadfastness",
    "continued planning",
    "drive and strategy",
    "crisis management",
    "negative outcome handling",
    "demonstrating courage",
    "embracing viewpoints",
    "emotional stability",
    "psychological flexibility",
    "adversity management",
    "challenge orientation",
    "growth through hardship",
    "resilient leadership",
    "emotional regulation",
    "recovery capacity",
    "stress resistance",
    "psychological capital",
  ],
  16: [
    "challenge assumptions",
    "holistic decision-making",
    "open minded",
    "holistic thinking",
    "systems thinking",
    "big picture view",
    "comprehensive perspective",
    "integrated approach",
    "multifaceted view",
    "wide-angle lens",
    "expansive thinking",
    "broad consideration",
    "inclusive perspective",
    "multiple perspectives",
    "different viewpoints",
    "resource utilization",
    "success orientation",
    "comprehensive analysis",
    "multidimensional thinking",
    "broad-spectrum analysis",
    "panoramic vision",
    "360-degree perspective",
    "integrative thinking",
    "comprehensive assessment",
    "all-encompassing view",
    "diverse considerations",
    "wide-ranging analysis",
    "expansive perspective",
    "inclusive outlook",
    "multifaceted approach",
  ],
  17: [
    "optimize resources",
    "strategic perspective",
    "resource allocation",
    "asset utilization",
    "budget management",
    "cost efficiency",
    "resource optimization",
    "efficient use",
    "sustainable management",
    "responsible allocation",
    "strategic distribution",
    "effective deployment",
    "long-term availability",
    "resource distribution",
    "preservation focus",
    "efficiency maximization",
    "resource conservation",
    "sustainable usage",
    "balanced allocation",
    "strategic resourcing",
    "capital efficiency",
    "effective allocation",
    "optimal deployment",
    "resource planning",
    "responsible stewardship",
    "resource efficiency",
    "allocation effectiveness",
    "distribution strategy",
    "sustainable management",
    "frugality",
    "cost-consciousness",
    "fiscal responsibility",
    "financial discipline",
    "economic value",
  ],
  18: [
    "crisis management",
    "preparedness",
    "crisis readiness",
    "emergency prepared",
    "risk ready",
    "risk management",
    "crisis prepared",
    "emergency ready",
    "risk oriented",
    "change ready",
    "future proof",
    "antifragile",
    "contingency planning",
    "preventive action",
    "preparedness",
    "readiness",
    "risk assessment",
    "precautionary measures",
    "emergency planning",
    "strategic preparedness",
    "operational readiness",
    "alert systems",
    "preparation strategies",
    "contingency development",
    "vulnerability analysis",
    "threat monitoring",
    "preventive protocols",
    "safety measures",
    "emergency frameworks",
    "disaster planning",
  ],
  19: [
    "crisis management",
    "response",
    "crisis management excellence",
    "emergency response ready",
    "rapid response capable",
    "crisis adaptable",
    "emergency adaptable",
    "crisis focused",
    "emergency focused",
    "adaptive organization",
    "rapid response",
    "emergency response",
    "disaster handling",
    "urgent action",
    "swift reaction",
    "immediate intervention",
  ],
  20: [
    "crisis management",
    "recovery",
    "crisis recovery focused",
    "bounce back ability",
    "recovery oriented",
    "resilience focused",
    "recovery excellence",
    "resilience driven",
    "organizational resilience",
    "bounce forward",
    "recovery planning",
    "rebuilding",
    "restoration",
  ],
  21: [
    "execution",
    "get stuff done",
    "hands-on delivery",
    "organization",
    "planning",
    "scheduling",
    "prioritization",
    "time management",
    "productivity",
    "effectiveness",
    "workflow optimization",
    "process management",
    "implementation",
    "output",
    "performance",
    "results",
    "accomplishment",
    "achievement",
    "yield",
    "production",
    "throughput",
    "operational effectiveness",
    "production optimization",
    "output maximization",
    "performance enhancement",
    "efficiency improvement",
    "timeliness",
    "thoroughness",
  ],
  22: [
    "inspect and collaborate",
    "learn through discovery",
    "information processing",
    "data handling",
    "knowledge management",
    "information filtering",
    "data analysis",
    "information organization",
    "knowledge utilization",
    "information synthesis",
    "data interpretation",
    "knowledge application",
    "relevant information seeking",
    "information optimization",
    "winning solutions",
    "multiple viewpoints",
    "information sensitivity",
    "random vs. meaningful",
    "timely transmission",
    "data discernment",
    "intelligent filtering",
    "strategic information use",
    "knowledge curation",
    "insight extraction",
    "information leverage",
    "data-driven decisions",
    "knowledge application",
    "information utilization",
    "strategic data use",
    "insight development",
  ],
  23: [
    "strategic perspective",
    "holistic decision-making",
    "data driven",
    "metrics driven",
    "data informed",
    "evidence based",
    "data first",
    "quantitative mindset",
    "analytics driven",
    "big data",
    "predictive analytics",
    "machine learning",
    "ai driven",
    "algorithmic decision making",
    "business intelligence",
    "data science",
    "insights oriented",
    "measurement focus",
    "metrics oriented",
    "kpi driven",
    "dashboard management",
    "data visualization",
    "data literacy",
    "data informed decisions",
    "analytical excellence",
    "insights generation",
    "quantitative focus",
    "metrics obsession",
    "measurable outcomes",
    "data centered",
    "analytics powered",
    "intelligent automation",
    "numerical thinking",
    "quantitative assessment",
    "statistical reasoning",
    "mathematical logic",
    "analytical processing",
    "data interpretation",
    "numerical problem solving",
    "strategic calculation",
    "analytical decision making",
  ],
  24: [
    "think big",
    "strategic perspective",
    "expanding the art of the possible",
    "strategic",
    "long-term vision",
    "big picture",
    "strategic mindset",
    "big picture focus",
    "strategic vision",
    "forward thinking",
    "strategic planning",
    "visionary approach",
    "strategic excellence",
    "long term thinking",
    "strategic orientation",
    "vision focused",
    "strategic leadership",
    "future oriented",
    "strategic direction",
    "visionary thinking",
    "strategic approach",
    "strategic planning focus",
    "visionary leadership",
    "future thinking",
  ],
  25: [
    "strategic perspective",
    "think big, dream big",
    "think big, go far",
    "strategic sophistication",
    "complex planning",
    "long-range strategy",
    "advanced planning",
    "strategic mastery",
    "comprehensive strategy",
    "multi-level planning",
    "strategic depth",
    "sophisticated approach",
    "well planned",
    "sequenced",
    "focused",
    "immediate context",
    "long-term requirements",
    "solutions for all stakeholders",
    "smart decisions",
    "long-term impacts",
    "bigger picture goals",
    "strategic wisdom",
    "planning excellence",
    "strategic excellence",
    "advanced foresight",
    "comprehensive planning",
    "strategic architecture",
    "master planning",
    "strategic brilliance",
    "complex problem-solving",
    "strategic genius",
    "executive-level thinking",
  ],
  26: [
    "execution",
    "get stuff done",
    "make stuff happen",
    "deliver results",
    "efficient",
    "accomplish",
    "make a mark and celebrate wins",
    "own the outcome",
    "own your results",
    "ship it",
    "deploy frequently",
    "rapid deployment",
    "continuous delivery",
    "sprint to success",
    "release early",
    "minimum viable product",
    "iterative improvement",
    "operational mastery",
    "execution excellence",
    "delivery focused",
    "results oriented",
    "outcome driven",
    "quality focused",
    "precision execution",
    "performance excellence",
    "delivery excellence",
    "operational excellence",
    "quality driven",
    "results achievement",
    "task mastery",
    "delivery oriented",
    "output focused",
    "performance driven",
    "execution mastery",
    "operational efficiency",
    "delivery precision",
    "quality execution",
  ],
  27: [
    "act fast, preempt, and experiment",
    "bias for action",
    "bias towards action",
    "proactive",
    "anticipate",
    "forward thinking",
    "foresight",
    "preventive action",
    "anticipation",
    "preparedness",
    "readiness",
    "advance planning",
    "prevention focus",
    "thinking ahead",
    "task preparation",
    "deadline anticipation",
    "well-prepared",
    "timely completion",
    "efficiency increase",
    "faster responses",
    "step ahead",
    "anticipatory action",
    "preventive measures",
    "future readiness",
  ],
  28: [
    "act like an owner",
    "be an owner of the mission",
    "ownership mentality",
    "act fast",
    "action",
    "bias for action",
    "delivery at pace",
    "preemptive",
    "take initiative",
    "self-starter",
    "be ambitious",
    "be brave",
    "be curious and bold",
    "be fearless",
    "believe and win",
    "bias for action, commit to iteration",
    "boldness",
    "courage to lead",
    "demonstrate urgency for action",
    "entrepreneurial spirit",
    "entrepreneurship",
    "fail, learn, and fix",
    "fast decision-making to adapt",
    "grit and curiosity for every challenge",
    "iterate and take risks",
    "lead the way",
    "no-limit mindset",
    "problem-solving self-starters",
    "run with it",
    "sense of urgency",
    "unflinching courage",
    "velocity",
    "founder mentality",
    "entrepreneurial DNA",
    "startup mindset",
    "bias for yes",
    "makers mentality",
    "builder's mindset",
    "create value",
    "value creation",
    "10x mindset",
    "founder dna",
    "entrepreneurial mindset",
    "startup mentality",
    "growth oriented",
    "bias towards yes",
    "maker mentality",
    "builder mindset",
    "value creator",
    "go getter attitude",
    "self starter mentality",
    "proactive mindset",
    "take charge attitude",
    "ownership mindset",
    "calculated risk taking",
    "bold action oriented",
    "drive for results",
    "results driven",
    "achievement focused",
    "performance oriented",
    "execution mindset",
    "get it done attitude",
    "bias for execution",
    "initiative",
    "self-starting",
    "taking action",
    "going first",
    "leading change",
    "starting projects",
    "taking the lead",
    "voluntary action",
    "self-motivation",
    "spontaneous action",
    "unprompted action",
    "self-directed activity",
    "independent action",
    "personal drive",
    "action orientation",
    "leadership initiative",
    "change catalyst",
    "opportunity seeker",
    "barrier breaker",
  ],
  29: [
    "client first",
    "client-focused",
    "positive impact",
    "impact",
    "make a difference",
    "results",
    "mission-driven",
    "purpose",
    "unlock opportunity",
    "results focus",
    "achievement orientation",
    "target-driven",
    "objective-focused",
    "success orientation",
    "target achievement",
    "goal attainment",
    "mission focus",
    "purpose-driven",
    "outcome orientation",
    "overarching mission",
    "goal achievement",
    "success focus",
    "target fixation",
    "performance drive",
    "achievement motivation",
    "mission-oriented",
    "purpose alignment",
    "objective achievement",
    "results generation",
  ],
  30: [
    "set lofty goals, with planning and accountability",
    "success is a partnership",
    "goal setting",
    "objective planning",
    "target development",
    "achievement planning",
    "success strategy",
    "performance management",
    "outcome planning",
    "results management",
    "achievement strategy",
    "objective management",
    "broad approach",
    "problem solving",
    "shift gear",
    "optimize outcomes",
    "complex situations",
    "external perspectives",
    "concrete goals",
    "goal achievement strategy",
    "performance planning",
    "objective optimization",
    "success engineering",
    "achievement architecture",
    "results planning",
    "target strategy",
    "outcome optimization",
    "achievement pathway",
    "performance framework",
    "success planning",
    "okrs",
    "smart goals",
    "growth metrics",
    "north star metric",
    "key results",
    "measurable outcomes",
    "impact driven",
    "outcome focused",
    "results oriented",
    "agile goal setting",
    "sprint planning",
    "iterative objectives",
    "milestone management",
    "target achievement",
    "performance indicators",
    "success metrics",
    "metric-driven goals",
    "stretch targets",
    "ambitious objectives",
    "achievement framework",
    "success criteria",
    "benchmarks",
    "performance standards",
    "success measures",
    "objective tracking",
    "goal attainment",
    "target management",
    "success pathway",
    "achievement roadmap",
    "performance excellence",
  ],
}

// Scenarios data
const SCENARIOS = [
  {
    id: "geopolitical",
    title: "Geopolitical",
    description: "Navigate complex global landscapes",
    image: "/v3/assets/geopolitical-scenario-icon.png",
  },
  {
    id: "business",
    title: "Business Case",
    description: "Tackle real-world corporate challenges",
    image: "/v3/assets/business-scenario-icon.png",
  },
]

const OnboardingMeasures: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const organization = useTypedSelector((state) => state.authSlice.organization)

  useEffect(() => {
    trackOnboardingStep("measures")
  }, [])

  // API hooks - fetch all measures from database
  const { data: measuresData, isLoading: isMeasuresLoading } = useGetMeasuresQuery()

  const { data: organizationMeasuresData } = useGetOrganizationMeasuresQuery(
    Number(organization?.id) || 0,
    {
      skip: !organization?.id,
    },
  )

  const { data: onboardingData } = useGetOnboardingCompletionQuery(
    Number(organization?.id) || 0,
    {
      skip: !organization?.id,
    },
  )

  const [createOrganizationMeasure, { isLoading: isCreatingMeasure }] =
    useCreateOrganizationMeasureMutation()

  const [updateSelectedScenario, { isLoading: isUpdatingScenario }] =
    useUpdateSelectedScenarioMutation()

  // Transform API measures to display format and sort by wheel order
  const MEASURES: MeasureDisplay[] = useMemo(() => {
    if (!measuresData?.data) return []
    return measuresData.data
      .map((measure) => ({
        id: measure.id,
        name: measure.measureName,
        color: measure.measureColor || DEFAULT_MEASURE_COLOR,
        definition: measure.description || "",
      }))
      .sort((a, b) => {
        const orderA = MEASURE_SORT_ORDER[a.name.toLowerCase()] ?? 999
        const orderB = MEASURE_SORT_ORDER[b.name.toLowerCase()] ?? 999
        return orderA - orderB
      })
  }, [measuresData])

  // State management
  const [selectedMeasures, setSelectedMeasures] = useState<Set<number>>(
    new Set(),
  )
  const [selectedScenario, setSelectedScenario] = useState<string>("")
  const [searchQuery, setSearchQuery] = useState("")
  const [searchResults, setSearchResults] = useState<
    Array<{ keyword: string; measureName: string; measureId: number }>
  >([])
  // Initialize step based on URL parameters - if ?step=2, start from scenarios
  const [currentStep, setCurrentStep] = useState<1 | 2>(() => {
    const stepParam = searchParams.get('step')
    return stepParam === '2' ? 2 : 1
  })
  const [hoveredMeasure, setHoveredMeasure] = useState<number | null>(null)
  const [showPricingModal, setShowPricingModal] = useState(false)
  const [dontShowPricingAgain, setDontShowPricingAgain] = useState(false)

  // Function to get icon path for a measure - maps by name to handle different DB orderings
  const getIconPath = (measure: (typeof MEASURES)[0]) => {
    // Map measure names (lowercase) to their icon filenames
    const iconMap: Record<string, string> = {
      "developmental orientation": "1. Developmental Orientation.png",
      "development orientation": "1. Developmental Orientation.png", // alias
      "dependability": "2. Dependability.png",
      "innovation": "3. Innovation.png",
      "creative decisiveness": "4. Creative Decisiveness.png",
      "creative flexibility": "5. Creative Flexibility.png",
      "workplace engagement": "6. Workplace Engagement.png",
      "people centrism": "7. People Centrism.png",
      "communication": "8. Communication.png",
      "empathetic communication": "9. Empathetic Communication.png",
      "collaboration": "10. Collaboration.png",
      "team orientation": "11 Team Orientation.png",
      "conflict approach": "12 Conflit Approach.png",
      "conflict response": "13. Conflict Response.png",
      "conflict management": "14. Conflict Management.png",
      "conflict resolution": "14. Conflict Management.png", // alias
      "emotional resilience": "15. Emotional Resilience.png",
      "breadth of approach": "16. Breadth of Approach.png",
      "resource management": "17. Resource Management.png",
      "crisis preparedness": "18. Crisis Preparedness.png",
      "crisis response": "19. Crisis Response.png",
      "crisis recovery": "20. Crisis Recovery.png",
      "task management": "21. Task Management.png",
      "information management": "22. Information Management.png",
      "strategic quantitative reasoning": "23. Strategic Quantitative Reasoning.png",
      "strategic quantitative thinking": "23. Strategic Quantitative Reasoning.png", // alias
      "strategic thinking": "24. Strategic Thinking.png",
      "advanced strategy": "25. Advanced Strategy.png",
      "productivity": "26. Productivity.png",
      "proactivity": "27. Proactivity.png",
      "initiative": "28. Initiative.png",
      "goal orientation": "29. Goal Orientation.png",
      "goal management": "30. Goal Management.png",
    }

    const filename = iconMap[measure.name.toLowerCase()]
    if (filename) {
      return `/v3/assets/measures/${filename}`
    }
    return null
  }

  // Load existing organization measures into selected state
  useEffect(() => {
    if (organizationMeasuresData?.data) {
      const existingMeasureIds = organizationMeasuresData.data
        .filter((om) => om.isActive)
        .map((om) => om.measureId)
      setSelectedMeasures(new Set(existingMeasureIds))
    }
  }, [organizationMeasuresData])

  // Load existing selected scenario from onboarding data
  useEffect(() => {
    if (onboardingData?.data?.selectedScenario) {
      setSelectedScenario(onboardingData.data.selectedScenario)
    }
  }, [onboardingData])

  // Handle search and generate keyword results
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([])
      return
    }

    const query = searchQuery.toLowerCase()
    const results: Array<{
      keyword: string
      measureName: string
      measureId: number
    }> = []

    // Search through all keywords for matches
    Object.entries(VALUE_KEYWORDS).forEach(([measureId, keywords]) => {
      const measure = MEASURES.find((m) => m.id === parseInt(measureId))
      if (!measure) return

      keywords.forEach((keyword) => {
        if (keyword.toLowerCase().includes(query)) {
          results.push({
            keyword,
            measureName: measure.name,
            measureId: measure.id,
          })
        }
      })

      // Also check measure name and definition
      if (
        measure.name.toLowerCase().includes(query) ||
        measure.definition.toLowerCase().includes(query)
      ) {
        results.push({
          keyword: measure.name,
          measureName: measure.name,
          measureId: measure.id,
        })
      }
    })

    setSearchResults(results.slice(0, 10)) // Limit to 10 results
  }, [searchQuery])

  const filteredMeasures = MEASURES.filter((measure) => {
    if (!searchQuery.trim()) return true

    const query = searchQuery.toLowerCase()
    const keywords =
      VALUE_KEYWORDS[measure.id as keyof typeof VALUE_KEYWORDS] || []

    return (
      measure.name.toLowerCase().includes(query) ||
      measure.definition.toLowerCase().includes(query) ||
      keywords.some((keyword) => keyword.toLowerCase().includes(query))
    )
  })

  const toggleMeasure = (measureId: number) => {
    const newSelected = new Set(selectedMeasures)
    if (newSelected.has(measureId)) {
      newSelected.delete(measureId)
      setSelectedMeasures(newSelected)
    } else {
      newSelected.add(measureId)
      setSelectedMeasures(newSelected)
      // Show pricing modal if selecting the 4th measure or beyond, and user hasn't opted out
      if (newSelected.size > 3 && !dontShowPricingAgain) {
        setShowPricingModal(true)
      }
    }
  }

  const handleNext = async () => {
    if (currentStep === 1 && selectedMeasures.size > 0) {
      setCurrentStep(2)
    } else if (currentStep === 2 && selectedScenario) {
      // Save selected measures to database
      try {
        if (!organization?.id) {
          console.error("No organization ID available")
          return
        }

        // Get existing organization measures to determine what to create/deactivate
        const existingMeasures = organizationMeasuresData?.data || []
        const existingMeasureIds = new Set(
          existingMeasures.map((m) => m.measureId),
        )
        const selectedArray = Array.from(selectedMeasures)

        // Create new measures that don't exist yet
        const newMeasures = selectedArray.filter(
          (measureId) => !existingMeasureIds.has(measureId),
        )

        console.log("Saving selected measures:", selectedArray)
        console.log("New measures to create:", newMeasures)

        // Create organization measures for newly selected ones
        for (const measureId of newMeasures) {
          await createOrganizationMeasure({
            organizationId: Number(organization.id),
            measureId,
            isActive: true,
          }).unwrap()
        }

        console.log("Measures saved successfully!")

        // Save selected scenario
        if (selectedScenario) {
          await updateSelectedScenario({
            organizationId: Number(organization.id),
            selectedScenario,
          }).unwrap()
          console.log("Selected scenario saved:", selectedScenario)
        }

        // Navigate to next step
        navigate("/onboarding/setup")
      } catch (error) {
        console.error("Error saving measures:", error)
        // Could add a toast notification here
      }
    }
  }

  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1)
    } else {
      navigate("/onboarding/password")
    }
  }

  // Helper function to create SVG path for wedge
  const createWedgePath = (
    centerX: number,
    centerY: number,
    radius: number,
    startAngle: number,
    endAngle: number,
  ) => {
    const start = polarToCartesian(centerX, centerY, radius, endAngle)
    const end = polarToCartesian(centerX, centerY, radius, startAngle)
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1"

    return [
      "M",
      centerX,
      centerY,
      "L",
      start.x,
      start.y,
      "A",
      radius,
      radius,
      0,
      largeArcFlag,
      0,
      end.x,
      end.y,
      "Z",
    ].join(" ")
  }

  const polarToCartesian = (
    centerX: number,
    centerY: number,
    radius: number,
    angleInDegrees: number,
  ) => {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0
    return {
      x: centerX + radius * Math.cos(angleInRadians),
      y: centerY + radius * Math.sin(angleInRadians),
    }
  }

  // Render the wheel component
  const renderWheel = () => {
    const totalMeasures = filteredMeasures.length
    const centerX = 250
    const centerY = 250
    const radius = 200
    const iconRadius = 230 // Move icons further out, beyond the wedges

    return (
      <div
        className="relative"
        style={{ width: "550px", height: "550px", overflow: "visible" }}>
        <svg
          width="500"
          height="500"
          className="absolute"
          style={{ left: "25px", top: "25px" }}>
          {filteredMeasures.map((measure, index) => {
            const angleSize = 360 / totalMeasures
            const startAngle = index * angleSize
            const endAngle = (index + 1) * angleSize
            const isSelected = selectedMeasures.has(measure.id)

            const path = createWedgePath(
              centerX,
              centerY,
              radius,
              startAngle,
              endAngle,
            )

            return (
              <path
                key={`wedge-${measure.id}`}
                d={path}
                fill={measure.color}
                fillOpacity={isSelected ? 0.9 : 0.6}
                stroke="white"
                strokeWidth="2"
                className="cursor-pointer transition-all duration-300"
                onClick={() => toggleMeasure(measure.id)}
                style={{
                  opacity: isSelected ? 1 : 0.7,
                  transition: "all 0.3s ease",
                }}
              />
            )
          })}
        </svg>

        {/* Icons */}
        {filteredMeasures.map((measure, index) => {
          const angleSize = 360 / totalMeasures
          const angle = index * angleSize + angleSize / 2
          const x = Math.cos((angle - 90) * (Math.PI / 180)) * iconRadius
          const y = Math.sin((angle - 90) * (Math.PI / 180)) * iconRadius
          const isSelected = selectedMeasures.has(measure.id)

          return (
            <div
              key={`icon-${measure.id}`}
              className={`absolute w-8 h-8 rounded-full border-2 border-white bg-white flex items-center justify-center cursor-pointer transition-all duration-300 shadow-lg ${
                isSelected ? "transform scale-105" : "hover:scale-105"
              }`}
              style={{
                left: `${x + 275}px`,
                top: `${y + 275}px`,
                transform: "translate(-50%, -50%)",
                boxShadow: isSelected
                  ? `0 0 15px ${measure.color}80`
                  : "0 2px 6px rgba(0,0,0,0.1)",
              }}
              onClick={() => toggleMeasure(measure.id)}
              title={measure.name}
              onMouseEnter={() => setHoveredMeasure(measure.id)}
              onMouseLeave={() => setHoveredMeasure(null)}>
              <img
                src={getIconPath(measure)!}
                alt={measure.name}
                style={{ width: "20px", height: "20px" }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement
                  target.style.display = "none"
                  const fallback = (e.target as HTMLElement)
                    .nextElementSibling as HTMLElement
                  if (fallback) fallback.style.display = "block"
                }}
              />
              <div
                style={{
                  display: "none",
                  fontSize: "12px",
                  color: "#333",
                  fontWeight: "bold",
                }}>
                {isSelected ? "✓" : measure.id}
              </div>

              {/* Tooltip */}
              {hoveredMeasure === measure.id && (
                <div
                  className="absolute bg-white border border-gray-200 shadow-lg text-gray-900 text-xs rounded-lg px-3 py-2 whitespace-nowrap pointer-events-none"
                  style={{
                    left:
                      (angle > 30 && angle < 150) ||
                      (angle > 290 && angle < 360)
                        ? "calc(-100% - 10px)"
                        : (angle > 150 && angle < 210) ||
                            (angle > 210 && angle < 330)
                          ? "calc(100% + 10px)"
                          : "50%",
                    top: angle > 120 && angle < 240 ? "45px" : "-45px",
                    transform:
                      (angle > 30 && angle < 150) ||
                      (angle > 290 && angle < 360) ||
                      (angle > 150 && angle < 210) ||
                      (angle > 210 && angle < 330)
                        ? "translateY(-50%)"
                        : "translateX(-50%)",
                    zIndex: 9999,
                  }}>
                  <div className="font-medium">{measure.name}</div>
                  {!(
                    (angle > 30 && angle < 150) ||
                    (angle > 290 && angle < 360) ||
                    (angle > 150 && angle < 210) ||
                    (angle > 210 && angle < 330)
                  ) && (
                    <div
                      className="absolute w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-white"
                      style={{
                        left: "50%",
                        top: angle > 120 && angle < 240 ? "-4px" : "100%",
                        transform: "translateX(-50%)",
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          )
        })}

        {/* Center circle */}
        <div
          className="absolute w-48 h-48 rounded-full bg-gradient-to-br from-white to-gray-50 border-4 border-gray-200 shadow-xl flex items-center justify-center z-20"
          style={{
            left: "275px",
            top: "275px",
            transform: "translate(-50%, -50%)",
          }}>
          <img
            src="/v3/assets/meseekna-brain.png"
            alt="Meseekna Brain Network"
            className="w-20 h-20"
          />
        </div>
      </div>
    )
  }

  // Show loading state while measures are being fetched
  if (isMeasuresLoading) {
    return (
      <div className="relative bg-base-white w-full h-[100dvh] flex flex-row items-start justify-start font-text-md-regular">
        <Sidebar />
        <div className="w-full h-[100dvh] flex-1 flex flex-col">
          <HeaderSection />
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="relative bg-base-white w-full h-[100dvh] flex flex-row items-start justify-start font-text-md-regular">
      <Sidebar />
      <div className="w-full h-[100dvh] flex-1 flex flex-col">
        <HeaderSection />
        <div className="bg-gray-50 relative box-border w-full max-w-[100dvw] h-full flex-1 flex flex-col justify-start py-6 px-9 gap-[19px] text-left text-sm text-gray-600 font-text-sm-medium border-r-[1px] border-solid border-gray-200 overflow-auto">
          {/* Progress indicator */}
          <OnboardingSteps currentStep={currentStep} />

          <div className="w-full max-w-7xl mx-auto">
            {currentStep === 1 ? (
              <>
                {/* Measures Selection */}
                <div className="mb-6 text-center">
                  <h1 className="text-4xl font-bold text-gray-900 mb-4 flex items-center justify-center gap-3">
                    Your Meseekna Measures
                    <div className="relative group">
                      <span
                        className="inline-flex items-center justify-center w-6 h-6 text-sm text-blue-600 border border-blue-600 rounded-full cursor-help"
                        title="Our measures include Task Management, Goal Management, Dependability, Communication, Creative Decisiveness, Strategic Thinking, and 24+ more validated dimensions"
                      >
                        ⓘ
                      </span>
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-4 py-3 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-50 shadow-xl w-72 text-center">
                        Our measures include Task Management, Goal Management, Dependability, Communication, Creative Decisiveness, Strategic Thinking, and 24+ more validated dimensions
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  </h1>
                  <p className="text-lg text-gray-600 mb-2">
                    Select which behavioral competencies to measure from our library of 30 dimensions validated by 500+ peer-reviewed publications. Search your company values to find matching measures.
                  </p>
                  <p className="text-sm text-gray-500 mb-6">
                    Hover over each measure icon to explore your options.
                  </p>
                </div>

                {/* Main content area */}
                <div className="flex gap-12 items-center justify-center">
                  {/* Wheel */}
                  <div className="flex-shrink-0 flex justify-center">
                    {renderWheel()}
                  </div>

                  {/* Selected measures */}
                  <div className="flex-1 max-w-xl">
                    {/* Search */}
                    <div className="mb-6 relative">
                      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4">
                        <input
                          type="text"
                          placeholder="Search your organization's values: i.e. innovation"
                          className="w-full px-6 py-4 text-lg border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 bg-gray-50"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                      </div>

                      {/* Search Results Dropdown */}
                      {searchQuery && searchResults.length > 0 && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                          {searchResults.map((result, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                              onClick={() => {
                                // Only add measure if not already selected, don't remove
                                const newSelected = new Set(selectedMeasures)
                                if (!newSelected.has(result.measureId)) {
                                  newSelected.add(result.measureId)
                                  setSelectedMeasures(newSelected)
                                  // Show pricing modal if selecting the 4th measure or beyond, and user hasn't opted out
                                  if (newSelected.size > 3 && !dontShowPricingAgain) {
                                    setShowPricingModal(true)
                                  }
                                }
                                setSearchQuery("")
                              }}>
                              <div className="flex items-center gap-3">
                                <div
                                  className="w-4 h-4 rounded-full border-2 border-white shadow-sm flex-shrink-0"
                                  style={{
                                    backgroundColor: MEASURES.find(
                                      (m) => m.id === result.measureId,
                                    )?.color,
                                  }}></div>
                                <span className="text-sm font-medium text-gray-900">
                                  {result.keyword}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-400">→</span>
                                <span className="text-sm text-gray-600">
                                  {result.measureName}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {searchQuery && (
                        <div className="mt-2">
                          <p className="text-sm text-gray-500">
                            Showing {filteredMeasures.length} of{" "}
                            {MEASURES.length} measures
                          </p>
                          {searchResults.length > 0 && (
                            <p className="text-xs text-blue-600 mt-1">
                              Found {searchResults.length} keyword matches
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
                      <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                        Selected Measures ({selectedMeasures.size})
                      </h3>

                      {selectedMeasures.size === 0 ? (
                        <div className="h-96 flex items-center justify-center">
                          <div className="text-center">
                            <p className="text-gray-500 italic text-lg mb-3">
                              No measures selected yet
                            </p>
                            <p className="text-gray-400 text-sm">
                              Each measure assesses specific cognitive processes like Task Management, Information Management, Creative Flexibility, and Strategic Thinking.
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4 h-96 overflow-y-auto pr-2">
                          {Array.from(selectedMeasures).map((measureId) => {
                            const measure = MEASURES.find(
                              (m) => m.id === measureId,
                            )
                            if (!measure) return null
                            return (
                              <div
                                key={measure.id}
                                className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 group hover:bg-gray-100 transition-colors">
                                <div
                                  className="w-10 h-10 rounded-full border-3 border-white shadow-sm flex-shrink-0 mt-0.5 flex items-center justify-center text-sm bg-white"
                                  style={{}}>
                                  <img
                                    src={getIconPath(measure)!}
                                    alt={measure.name}
                                    style={{ width: "24px", height: "24px" }}
                                    onError={(e) => {
                                      const target =
                                        e.target as HTMLImageElement
                                      target.style.display = "none"
                                      const fallback = (e.target as HTMLElement)
                                        .nextElementSibling as HTMLElement
                                      if (fallback)
                                        fallback.style.display = "block"
                                    }}
                                  />
                                  <div
                                    style={{
                                      display: "none",
                                      fontSize: "10px",
                                      color: "white",
                                      fontWeight: "bold",
                                    }}>
                                    {measure.id}
                                  </div>
                                </div>
                                <div className="flex-1">
                                  <h4 className="font-semibold text-gray-900 text-lg">
                                    {measure.name}
                                  </h4>
                                  <p className="text-gray-600 mt-1 leading-relaxed">
                                    {measure.definition}
                                  </p>
                                </div>
                                <button
                                  onClick={() => toggleMeasure(measure.id)}
                                  className="flex-shrink-0 w-6 h-6 rounded-full bg-red-100 hover:bg-red-200 text-red-600 hover:text-red-700 flex items-center justify-center transition-colors"
                                  title="Remove measure">
                                  <span className="text-sm font-bold">×</span>
                                </button>
                              </div>
                            )
                          })}
                        </div>
                      )}

                      <div className="mt-6 pt-4 border-t border-gray-200">
                        <button
                          onClick={selectedMeasures.size > 0 ? handleNext : undefined}
                          disabled={selectedMeasures.size === 0}
                          className={`w-full font-semibold py-3 px-6 rounded-lg transition-colors ${
                            selectedMeasures.size > 0
                              ? "bg-blue-600 hover:bg-blue-700 text-white cursor-pointer"
                              : "bg-gray-300 text-gray-500 cursor-not-allowed"
                          }`}>
                          Continue to Scenarios
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Scenario Selection */}
                <div className="mb-6 text-center">
                  <button
                    onClick={handleBack}
                    className="text-blue-600 hover:text-blue-800 mb-4 font-medium">
                    ← Back to Measures
                  </button>
                  <h1 className="text-4xl font-bold text-gray-900 mb-4">
                    Choose Your Scenario
                  </h1>
                  <p className="text-lg text-gray-600 mb-4">
                    Select the perfect backdrop for your team&apos;s growth.
                    Choose between three exciting scenario types to challenge
                    and inspire your participants.
                  </p>
                  <p className="text-md text-blue-600 font-medium mb-6">
                    All scenarios measure the same competencies - choose the story that best fits your team&apos;s interests.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-8 mb-8 max-w-4xl mx-auto">
                  {SCENARIOS.map((scenario) => (
                    <div
                      key={scenario.id}
                      role="button"
                      tabIndex={0}
                      aria-pressed={selectedScenario === scenario.id}
                      onClick={() => setSelectedScenario(scenario.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault()
                          setSelectedScenario(scenario.id)
                        }
                      }}
                      className={`relative cursor-pointer transition-all duration-300 rounded-xl shadow-lg border-2 p-6 text-center hover:transform hover:-translate-y-1 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                        selectedScenario === scenario.id
                          ? "bg-blue-50 border-blue-500 shadow-blue-200 ring-2 ring-blue-500"
                          : "bg-white border-gray-200"
                      }`}>
                      {scenario.id === "business" && (
                        <div className="absolute -top-2 -right-2 bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-semibold z-10">
                          Most Popular
                        </div>
                      )}
                      <div className="w-24 h-24 mx-auto mb-4 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center">
                        <img
                          src={scenario.image}
                          alt={scenario.title}
                          className="w-16 h-16 object-contain"
                        />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        {scenario.title}
                      </h3>
                      <p className="text-gray-600">{scenario.description}</p>
                    </div>
                  ))}
                </div>

                {/* Custom Scenario Note */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
                  <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center gap-2">
                    <img
                      src="/v3/assets/settings015.svg"
                      alt="Settings"
                      className="w-6 h-6"
                    />
                    Custom Scenarios Available
                  </h3>
                  <p className="text-blue-800 mb-4 leading-relaxed">
                    Need something tailored to your specific industry, challenge, or organizational context?
                    Custom scenarios offer unlimited flexibility to create the perfect assessment experience for your team.
                  </p>
                  <div className="bg-white rounded-lg p-4 mb-4">
                    <h4 className="font-medium text-blue-900 mb-2">What to expect:</h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• <strong>Timeline:</strong> 1 week deployment after onboarding call</li>
                      <li>• <strong>Process:</strong> Consultation call to understand your requirements</li>
                      <li>• <strong>Investment:</strong> Additional cost based on complexity</li>
                      <li>• <strong>Support:</strong> Dedicated implementation specialist</li>
                    </ul>
                  </div>
                  <p className="text-blue-800 text-sm">
                    Interested in exploring custom options? Contact us at{" "}
                    <span className="font-medium text-blue-900">info@meseekna.com</span>
                  </p>
                </div>

                {selectedScenario && (
                  <div className="text-center">
                    <button
                      onClick={handleNext}
                      disabled={isCreatingMeasure || isUpdatingScenario}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed">
                      {isCreatingMeasure || isUpdatingScenario
                        ? "Saving..."
                        : "Continue"}
                    </button>
                  </div>
                )}
              </>
            )}

            {/* Navigation buttons - only show on measures step */}
            {currentStep === 1 && (
              <div className="flex justify-between items-center mt-8">
                <button
                  onClick={handleBack}
                  className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors">
                  Back
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Pricing Modal */}
      {showPricingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Additional Measures Pricing</h3>
              <p className="text-gray-600 mb-4">
                You&apos;ve selected more than 3 measures. Additional measures are <span className="font-semibold text-gray-900">$8 each per month</span>.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-800">
                  <strong>Current selection:</strong> {selectedMeasures.size} measures<br/>
                  <strong>Additional cost:</strong> ${(selectedMeasures.size - 3) * 8}/month
                </p>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                Questions about pricing? Contact us at{" "}
                <a
                  href="mailto:info@meseekna.com"
                  className="text-blue-600 hover:text-blue-800 underline">
                  info@meseekna.com
                </a>
              </p>

              {/* Do not show again checkbox */}
              <div className="flex items-center gap-3 mb-6 p-3 bg-gray-50 rounded-lg">
                <input
                  type="checkbox"
                  id="dontShowAgain"
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  checked={dontShowPricingAgain}
                  onChange={(e) => setDontShowPricingAgain(e.target.checked)}
                />
                <label htmlFor="dontShowAgain" className="text-sm text-gray-700 cursor-pointer">
                  Don&apos;t show this message again
                </label>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowPricingModal(false)}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors">
                I Understand
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default OnboardingMeasures
